import BigNumber from 'bignumber.js';
import { useQuery } from 'react-query';
import { NAVIGATION_ENDPOINTS } from '../constants/urls';
import { fetchWrapper } from '../utils';

export type Formatters = {
  date: (input: string) => string | undefined;
  currency: (amount: any, currency?: string) => string;
};

export const getCharacterOfEmptyData = () => '—';

export const useFormatters = ({
  locale: localeOverride,
}: {
  locale: string;
}): Formatters => {
  const locale = localeOverride.replace('_', '-');

  const { data: currencies } = useQuery(
    'Currencies',
    async () => {
      const body = await fetchWrapper<{
        currencies: { isActive: boolean; code: string; isDefault?: boolean }[];
      }>(NAVIGATION_ENDPOINTS.V1_ORDERS_CURRENCIES);

      return body?.currencies?.filter(({ isActive }) => isActive) ?? [];
    },
    { placeholderData: [], notifyOnChangeProps: ['data'], staleTime: Infinity },
  );

  const isDateWithoutTime = (dateStr: string) => {
    // Eg: "YYYY-MM-DD" (ISO-8601)
    return dateStr.length === 10 && dateStr[4] === '-' && dateStr[7] === '-';
  };

  const getDate = (dateTime: string) => {
    const timePostfix = 'T00:00:00';
    const value = isDateWithoutTime(dateTime)
      ? `${dateTime}${timePostfix}`
      : dateTime;

    return new Date(value);
  };

  return {
    date: (date: string) => {
      try {
        return new Intl.DateTimeFormat(locale).format(getDate(date));
      } catch (err) {
        console.error(err);
        return undefined;
      }
    },
    currency: (amountToFormat?: any, currency?: string): string => {
      if (amountToFormat === undefined) {
        return getCharacterOfEmptyData();
      }

      const amount = `${amountToFormat}`;

      const currencyMeta =
        (currencies ?? []).find(({ code }) => code === currency) ??
        (currencies ?? []).find(({ isDefault }) => isDefault === true);

      const fallbackParsedAmount: any = (() => {
        const amountNumber = parseFloat(amount);
        return isNaN(amountNumber) ? amount : amountNumber.toFixed(2);
      })();

      // source: https://git.zias.io/application/order-ui-client/blob/a45644610c88f9fe0c95f6f31775d897ed2b8ed6/src/app/order/util/CurrencyUtil.ts
      const formattedAmount = currencyMeta
        ? new BigNumber(amount)
            .decimalPlaces(
              currencyMeta.defaultDecimalPlaces ?? 2,
              (() => {
                switch (currencyMeta.defaultRoundingMode) {
                  case 'HALF_UP':
                    return BigNumber.ROUND_HALF_UP;
                  case 'UP':
                    return BigNumber.ROUND_UP;
                  case 'DOWN':
                    return BigNumber.ROUND_DOWN;
                }
              })(),
            )
            .toNumber()
        : fallbackParsedAmount;

      return isNaN(formattedAmount)
        ? getCharacterOfEmptyData()
        : new Intl.NumberFormat(locale, {
            minimumFractionDigits: currencyMeta?.defaultDecimalPlaces ?? 2,
            maximumFractionDigits: currencyMeta?.defaultDecimalPlaces ?? 2,
            style: currencyMeta ? 'currency' : 'decimal',
            currency: currencyMeta?.code,
          }).format(formattedAmount);
    },
  };
};
